// B4-C: helpers for documents issued in a foreign currency (the ledger stays BDT).

export const DOCUMENT_CURRENCIES = ['BDT', 'USD', 'EUR', 'GBP', 'INR', 'CNY', 'JPY', 'AED', 'SAR', 'SGD', 'AUD', 'CAD', 'CHF'];

export function isForeignCurrency(code?: string | null): boolean {
    return !!code && code !== 'BDT';
}

const num = (n: number, dp: number) =>
    n.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });

/** "USD 12,500.00 at 121.50 = BDT 1,518,750.00" */
export function formatFcAtRate(code: string, fc: number, rate: number, bdt: number): string {
    return `${code} ${num(fc, 2)} at ${rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })} = BDT ${num(bdt, 2)}`;
}

/** Foreign amount with its code, e.g. "USD 12,500.00". */
export function formatFc(code: string, fc?: number | null): string {
    return `${code} ${num(fc ?? 0, 2)}`;
}

/** A foreign unit price with its code: at least 2dp, up to 4 kept ("USD 6.60", "USD 412.125"). */
export function formatFcPrice(code: string, price?: number | null): string {
    return `${code} ${(price ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

/** Open foreign balances ({USD: 100}) as "USD 100.00, EUR 5.00"; empty when none. */
export function formatForeignMap(m?: Record<string, number> | null): string {
    return Object.entries(m ?? {}).filter(([, v]) => v !== 0).map(([c, v]) => formatFc(c, v)).join(', ');
}

/**
 * G5b: the amount discount of a stored foreign line, in the document currency (the server keeps only the foreign net):
 * foreign gross (quantity x foreign unit price, 2 dp) less the foreign net. Null when the line has no foreign figures.
 */
export function foreignLineDiscount(quantity: number, foreignUnitPrice?: number | null, foreignAmount?: number | null): number | null {
    if (foreignUnitPrice == null || foreignAmount == null) return null;
    const gross = Math.round(quantity * foreignUnitPrice * 100) / 100;
    return Math.max(0, Math.round((gross - foreignAmount) * 100) / 100);
}

/** G5b: one toast for a save: the soft warnings (rate far from the table, export overlap) ride on it. */
export function toastSaved(toast: { showSuccess: (m: string) => void; showWarning?: (m: string) => void },
                           message: string, warnings?: string[] | null): void {
    const w = Array.from(new Set(warnings ?? []));
    if (w.length > 0 && toast.showWarning) toast.showWarning(`${message}. ${w.join(' ')}`);
    else toast.showSuccess(message);
}

/**
 * Polish 4: the unit price a catalogue pick fills on a document line. The catalogue price is BDT; a foreign document
 * converts it at the document rate (BDT / rate, rounded once to 2 dp). With no usable rate the price is
 * left blank (NaN) rather than showing the BDT figure as if it were foreign.
 */
export function catalogueUnitPrice(bdtPrice: number, currency: string, rate: number | '' | null | undefined): number {
    if (!isForeignCurrency(currency)) return bdtPrice;
    const r = Number(rate);
    if (!(r > 0)) return NaN;
    return Math.round((bdtPrice / r) * 100) / 100;
}

/**
 * Polish 4: re-price the lines whose price still comes from the catalogue (catalogueBdtPrice set, i.e. the user has
 * not typed a price since the pick) after the document currency or rate changes.
 */
export function repriceCatalogueLines<T extends { unitPrice: number; catalogueBdtPrice?: number | null }>(
    rows: T[], currency: string, rate: number | '' | null | undefined): T[] {
    return rows.map(r => (r.catalogueBdtPrice == null ? r : { ...r, unitPrice: catalogueUnitPrice(r.catalogueBdtPrice, currency, rate) }));
}
